import { create } from 'zustand';

export interface VehiclePosition {
    vehicleName: string;
    lon: number;
    lat: number;
    heading: number;
    speed: number;
    timestamp: number;
}

export interface VehicleStatus {
    vehicleName: string;
    status: 'ONLINE' | 'OFFLINE' | 'RUNNING' | 'IDLE' | 'CHARGING' | 'ERROR' | 'WITHORDERS' | 'ONWAY' | 'ATSTOP' | 'BACK' | 'ARRIVEHOME' | 'ROUTING';
    battery: number;
    currentTask?: string;
    lastUpdate: number;
}

export interface VehicleState {
    // 车辆位置数据
    positions: Map<string, VehiclePosition>;
    // 车辆状态数据
    statuses: Map<string, VehicleStatus>;
    // 选中的车辆
    selectedVehicle: string | null;
    // 是否正在加载
    loading: boolean;

    // Getters
    getPosition: (vehicleName: string) => VehiclePosition | undefined;
    getStatus: (vehicleName: string) => VehicleStatus | undefined;

    // Actions
    updatePosition: (vehicleName: string, position: Omit<VehiclePosition, 'vehicleName'>) => void;
    updateStatus: (vehicleName: string, status: Omit<VehicleStatus, 'vehicleName'>) => void;
    batchUpdatePositions: (updates: VehiclePosition[]) => void;
    batchUpdateStatuses: (updates: VehicleStatus[]) => void;
    selectVehicle: (vehicleName: string | null) => void;
    setLoading: (loading: boolean) => void;
    clearAll: () => void;
}

export const useVehicleStore = create<VehicleState>((set, get) => ({
    positions: new Map(),
    statuses: new Map(),
    selectedVehicle: null,
    loading: false,

    getPosition: (vehicleName: string) => get().positions.get(vehicleName),
    getStatus: (vehicleName: string) => get().statuses.get(vehicleName),

    updatePosition: (vehicleName, position) =>
        set((state) => {
            const newPositions = new Map(state.positions);
            newPositions.set(vehicleName, { vehicleName, ...position });
            return { positions: newPositions };
        }),

    updateStatus: (vehicleName, status) =>
        set((state) => {
            const newStatuses = new Map(state.statuses);
            newStatuses.set(vehicleName, { vehicleName, ...status });
            return { statuses: newStatuses };
        }),

    batchUpdatePositions: (updates) =>
        set((state) => {
            const newPositions = new Map(state.positions);
            updates.forEach((update) => {
                newPositions.set(update.vehicleName, update);
            });
            return { positions: newPositions };
        }),

    batchUpdateStatuses: (updates) =>
        set((state) => {
            const newStatuses = new Map(state.statuses);
            updates.forEach((update) => {
                newStatuses.set(update.vehicleName, update);
            });
            return { statuses: newStatuses };
        }),

    selectVehicle: (vehicleName) => set({ selectedVehicle: vehicleName }),

    setLoading: (loading) => set({ loading }),

    clearAll: () =>
        set({
            positions: new Map(),
            statuses: new Map(),
            selectedVehicle: null,
        }),
}));
